import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { compact, first, isBoolean, last } from 'lodash';
import { Subject } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { KanbanAbstract } from '@app/modules/ui-components/abstract/kanban.abstract';
import { UserService } from '@app/services/user.service';
import { selectTurnoversFilters } from '@dashboards/store';
import { selectFinishTypesEnumeration } from '@main-application/store/selectors/enumeration.selectors';
import { selectUserData } from '@main-application/store/selectors/user.selectors';
import {
  setSectionLostRevenue,
  toggleAllSections,
  toggleInactiveFilteredSingleSection,
  toggleSingleSection,
} from '@main-application/turnovers/store/actions/turnovers.actions';
import { setTurnoverOverviewSectionCounters } from '@main-application/turnovers/store/reducers/turnovers-overview.reducer';
import {
  selectIsActiveSection,
  selectIsInactiveFilteredSection,
} from '@main-application/turnovers/store/selectors/turnovers.selectors';
import { AppRoutes } from '@shared/constants/app-routes.const';
import { EColorPalette } from '@shared/enums/color-palette.enum';
import { EIcon } from '@shared/enums/icon.enum';
import { LabelEnumType } from '@shared/enums/label.enum';
import { PriorityType } from '@shared/enums/priority-type';
import { RouteData } from '@shared/enums/route-data';
import { WorkflowStepEnumType } from '@shared/enums/workflow-step.enum';
import { checkKanbanFilterEmpty } from '@shared/functions/check-kanban-filter-empty';
import { getCurrentWorkflowStep } from '@shared/functions/get-current-workflow-step.function';
import { getEnumerationRadioListFunction } from '@shared/functions/get-enumeration-radio-list.function';
import { getTimeInStep } from '@shared/functions/get-time-in-step.function';
import { paramsCompare } from '@shared/functions/params-compare.function';
import { ScrollIntoHTMLElement } from '@shared/functions/scroll-into-element.function';
import { KanbanListItem } from '@shared/interfaces/kanban-list-item.interface';
import { ParamsCompareFunc, ParamsCompareItem } from '@shared/interfaces/params-compare-item';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { StepsBeforeTask, TaskInspectionItem } from '@shared/interfaces/task-inspection-item.interface';
import { RestTurnoverTaskModel, RestWorkflowInstanceModel } from '@shared/interfaces/turnover.interface';
import { TurnoversFilters } from '@shared/interfaces/turnovers-filters.interface';
import { UserData } from '@shared/interfaces/user-data';
import { RestUserModel } from '@shared/interfaces/user.interface';
import { DaysInStepPipe } from '@shared/pipes/days-in-step.pipe';
import { LostRevenuePipe } from '@shared/pipes/lost-revenue.pipe';

export interface RestTurnoverTaskModelExt {
  item: RestTurnoverTaskModel;
  visible: boolean;
}

export const NOT_READY_COLUMNS_SIZE = 7;
export const READY_COLUMNS_SIZE = 2;

@UntilDestroy()
@Component({
  selector: 'app-kanban-section',
  templateUrl: './kanban-section.component.html',
  styleUrls: ['./kanban-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KanbanSectionComponent extends KanbanAbstract implements OnInit, AfterViewInit, OnDestroy {
  protected cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private store: Store<{}> = inject(Store);
  private router: Router = inject(Router);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private daysInStep: DaysInStepPipe = inject(DaysInStepPipe);
  private lostRevenue: LostRevenuePipe = inject(LostRevenuePipe);
  private userService: UserService = inject(UserService);

  EColorPalette = EColorPalette;
  EIcon = EIcon;
  declare turnoversFilters: TurnoversFilters;
  isFiltersEmpty: boolean;
  previewContentCss = null;
  previewCss = '';
  stepsBeforeTask: StepsBeforeTask;
  evictionSection: RestTurnoverTaskModelExt[] = [];
  noticeSection: RestTurnoverTaskModelExt[] = [];
  moveOutSection: RestTurnoverTaskModelExt[] = [];
  makeReadySection: RestTurnoverTaskModelExt[] = [];
  renoSection: RestTurnoverTaskModelExt[] = [];
  punchSection: RestTurnoverTaskModelExt[] = [];
  approvalSection: RestTurnoverTaskModelExt[] = [];
  leaseUpSection: RestTurnoverTaskModelExt[] = [];
  moveInSection: RestTurnoverTaskModelExt[] = [];
  sectionItemsCount = 0;
  allUsers: RestUserModel[];
  declare finishList: IRadioButtonOption<number>[];
  sectionId = '';
  sectionLostRevenue = 0;
  isExpandedByFilter = false;
  isAllExpanded = false;
  isFilteredInactive = false;

  @Input() kanbanListItem: KanbanListItem;
  @Input() containerCss = '';
  @Input() isJoined = false;
  @Input() declare sectionItemsAmount$: Subject<{ sectionId: string; amount: number }>;
  @Input() hiddenReadyColumns: Set<WorkflowStepEnumType> = new Set();
  @Input() hiddenNotReadyColumns: Set<WorkflowStepEnumType> = new Set();
  @Input() extendMakeReadyColumn = false;

  @ViewChild('sectionContainer', { read: ElementRef }) sectionContainer: ElementRef;
  @ViewChild('sectionContent', { read: ElementRef }) sectionContent: ElementRef<HTMLElement>;

  get isExpandedEffective() {
    return this.isFiltersEmpty ? this.isExpanded : !this.isFilteredInactive;
  }

  get kanbanNotReadySize() {
    if (this.extendMakeReadyColumn) {
      return NOT_READY_COLUMNS_SIZE - this.hiddenNotReadyColumns.size + 2;
    }
    return NOT_READY_COLUMNS_SIZE - this.hiddenNotReadyColumns.size;
  }

  get kanbanReadySize() {
    return READY_COLUMNS_SIZE - this.hiddenReadyColumns.size;
  }

  constructor() {
    super();
  }

  ngAfterViewInit(): void {
    const sectionParams = this.activatedRoute.snapshot.queryParams[RouteData.KANBAN_SECTION];
    this.checkSectionToExpand(parseInt(sectionParams));
  }

  ngOnInit(): void {
    this.sectionId = `kanban-section-${this.kanbanListItem.propertyId}`;
    this.isFiltersEmpty = checkKanbanFilterEmpty(this.turnoversFilters);

    this.store
      .select(selectUserData)
      .pipe(
        untilDestroyed(this),
        filter((userData: UserData) => !!userData),
        tap((userData: UserData) => {
          this.userData = userData;
        })
      )
      .subscribe()
      .untilDestroyed(this);

    this.userService
      .getCachedUserList(true)
      .pipe(
        untilDestroyed(this),
        tap(allUsers => {
          this.allUsers = allUsers;
        })
      )
      .subscribe()
      .untilDestroyed(this);

    this.store
      .select(selectFinishTypesEnumeration)
      .pipe(
        untilDestroyed(this),
        tap(finishEnumeration => {
          this.finishList = getEnumerationRadioListFunction(finishEnumeration, '', false);
          this.cdr.markForCheck();
        })
      )
      .subscribe()
      .untilDestroyed(this);

    this.store
      .select(selectTurnoversFilters)
      .pipe(
        untilDestroyed(this),
        filter((turnoversFilters: TurnoversFilters) => !!turnoversFilters),
        tap((turnoversFilters: TurnoversFilters) => {
          this.turnoversFilters = turnoversFilters;
          this.isFiltersEmpty = checkKanbanFilterEmpty(this.turnoversFilters);
          this.prepareKanbanList();
          this.cdr.detectChanges();
        })
      )
      .subscribe()
      .untilDestroyed(this);

    this.store
      .select(selectIsActiveSection(this.kanbanListItem.propertyId))
      .pipe(
        untilDestroyed(this),
        tap(isExpanded => {
          this.isExpanded = isExpanded;
          if (this.isFiltersEmpty) {
            if (this.isExpandedEffective) {
              this.setSectionLostRevenue(this.sectionLostRevenue);
            } else {
              this.setSectionLostRevenue(0);
            }
          }

          this.cdr.markForCheck();
        })
      )
      .subscribe()
      .untilDestroyed(this);

    this.store
      .select(selectIsInactiveFilteredSection(this.kanbanListItem.propertyId))
      .pipe(
        untilDestroyed(this),
        tap(isFilteredInactive => {
          this.isFilteredInactive = isFilteredInactive;

          if (!this.isFiltersEmpty) {
            if (this.isExpandedEffective) {
              this.setSectionLostRevenue(this.sectionLostRevenue);
            } else {
              this.setSectionLostRevenue(0);
            }
          }
          this.cdr.markForCheck();
        })
      )
      .subscribe()
      .untilDestroyed(this);
  }

  private prepareKanbanList() {
    if (!this.kanbanListItem.turnoversList) return;

    this.toggleLoading(true);
    this.evictionSection = [];
    this.noticeSection = [];
    this.moveOutSection = [];
    this.makeReadySection = [];
    this.renoSection = [];
    this.punchSection = [];
    this.approvalSection = [];
    this.leaseUpSection = [];
    this.moveInSection = [];

    (this.kanbanListItem.turnoversList || [])
      .filter(
        (propertyTask: RestTurnoverTaskModel) =>
          !propertyTask.turnoverData?.isCanceled && !propertyTask.turnoverData?.isCompleted
      )
      .filter(
        (propertyTask: RestTurnoverTaskModel) =>
          !this.hiddenNotReadyColumns.has(propertyTask.turnoverData.activeWorkflowStepType) ||
          !this.hiddenReadyColumns.has(propertyTask.turnoverData.activeWorkflowStepType)
      )
      .forEach((propertyTask: RestTurnoverTaskModel) => {
        switch (propertyTask.turnoverData.activeWorkflowStepType) {
          case WorkflowStepEnumType.Notice:
            {
              this.checkFilter(propertyTask, this.evictionSection, true);
              this.checkFilter(propertyTask, this.noticeSection, false);
            }
            break;
          case WorkflowStepEnumType.MoveOut:
            {
              this.checkFilter(propertyTask, this.moveOutSection);
            }
            break;
          case WorkflowStepEnumType.MakeReady:
            {
              this.checkFilter(propertyTask, this.makeReadySection);
            }
            break;
          case WorkflowStepEnumType.Reno:
            {
              this.checkFilter(propertyTask, this.renoSection);
            }
            break;
          case WorkflowStepEnumType.Punch:
            {
              this.checkFilter(propertyTask, this.punchSection);
            }
            break;
          case WorkflowStepEnumType.Approval:
            {
              this.checkFilter(propertyTask, this.approvalSection);
            }
            break;
          case WorkflowStepEnumType.LeaseUp:
            {
              this.checkFilter(propertyTask, this.leaseUpSection);
            }
            break;
          case WorkflowStepEnumType.MoveIn:
            {
              this.checkFilter(propertyTask, this.moveInSection);
            }
            break;
        }
      });

    this.sortSections();

    const allSections = [
      ...this.evictionSection,
      ...this.noticeSection,
      ...this.moveOutSection,
      ...this.makeReadySection,
      ...this.renoSection,
      ...this.punchSection,
      ...this.approvalSection,
      ...this.leaseUpSection,
      ...this.moveInSection,
    ].filter(i => i.visible);

    this.sectionItemsCount = allSections.length;

    this.sectionLostRevenue = allSections.reduce((sum, item) => sum + this.getLostRevenue(item.item), 0);

    if (this.isExpandedEffective) {
      this.setSectionLostRevenue(this.sectionLostRevenue);
    }

    this.toggleLoading(false);

    this.sectionItemsAmount$.next({ sectionId: this.sectionId, amount: this.sectionItemsCount });
    this.store.dispatch(
      setTurnoverOverviewSectionCounters({
        propertyId: this.kanbanListItem.propertyId,
        counters: {
          [WorkflowStepEnumType.Eviction]: this.evictionSection.filter(i => i.visible).length,
          [WorkflowStepEnumType.Notice]: this.noticeSection.filter(i => i.visible).length,
          [WorkflowStepEnumType.MoveOut]: this.moveOutSection.filter(i => i.visible).length,
          [WorkflowStepEnumType.MakeReady]: this.makeReadySection.filter(i => i.visible).length,
          [WorkflowStepEnumType.Reno]: this.renoSection.filter(i => i.visible).length,
          [WorkflowStepEnumType.Punch]: this.punchSection.filter(i => i.visible).length,
          [WorkflowStepEnumType.Approval]: this.approvalSection.filter(i => i.visible).length,
          [WorkflowStepEnumType.LeaseUp]: this.leaseUpSection.filter(i => i.visible).length,
          [WorkflowStepEnumType.MoveIn]: this.moveInSection.filter(i => i.visible).length,
        },
      })
    );
  }

  setSectionLostRevenue(lostRevenue) {
    this.store.dispatch(
      setSectionLostRevenue({
        propertyId: this.kanbanListItem.propertyId,
        lostRevenue,
      })
    );
  }

  toggleMenu() {
    if (!this.isFiltersEmpty) {
      this.store.dispatch(
        toggleInactiveFilteredSingleSection({
          propertyId: this.kanbanListItem.propertyId,
          active: !this.isFilteredInactive,
        })
      );

      return;
    }

    this.store.dispatch(toggleSingleSection({ propertyId: this.kanbanListItem.propertyId, active: !this.isExpanded }));
    setTimeout(() => {
      if (this.sectionContent) {
        this.sectionContent.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 500);
  }

  getLostRevenue(item: RestTurnoverTaskModel) {
    const stepsBeforeTask = this.getStepBeforeTask(item);
    const forAllProcess = true;
    const daysInStep = this.daysInStep.transform(
      // workflowStepEnumType not needed when forAllProcess = true
      {} as WorkflowStepEnumType,
      stepsBeforeTask.timerInstance,
      forAllProcess
    );

    const rent = item?.turnoverData?.newRent || item?.turnoverData?.marketRent;
    return this.lostRevenue.transform(daysInStep, rent);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.store.dispatch(setSectionLostRevenue({ propertyId: this.kanbanListItem.propertyId, lostRevenue: 0 }));
  }

  private checkFilter(item: RestTurnoverTaskModel, section: RestTurnoverTaskModelExt[], isEviction?: boolean) {
    const notHidden =
      !this.hiddenNotReadyColumns.has(item.turnoverData.activeWorkflowStepType) &&
      !this.hiddenReadyColumns.has(item.turnoverData.activeWorkflowStepType);
    if (this.isFiltersEmpty) {
      const shouldAddItem = !isBoolean(isEviction) || isEviction === item.turnoverData?.hasEvictLabel;

      if (shouldAddItem) {
        section.push({
          item,
          visible: notHidden,
        });
      }
    } else {
      const compareParams: (ParamsCompareItem | ParamsCompareFunc)[] = [];

      if (this.turnoversFilters?.unitName) {
        const assignee = this.allUsers.find(x => x.id === item?.assigneeId);
        const finish = this.finishList.find(
          x => x.value === item?.turnoverData.newFinish || x.value === item?.turnoverData.oldFinish
        );
        const compareWith = this.turnoversFilters.unitName.toLowerCase();
        compareParams.push({
          currentValue: compact([
            item?.unitName,
            item?.propertyName,
            assignee?.displayName,
            item?.unitType,
            finish?.label,
          ]).some(x => x.toLowerCase().includes(compareWith)),
          expectValue: true,
        });
      }

      if (this.turnoversFilters?.assignee) {
        compareParams.push({ currentValue: item.assigneeId, expectValue: this.turnoversFilters?.assignee });
      }

      if (this.turnoversFilters.unitType?.length) {
        compareParams.push({
          currentValue: this.turnoversFilters.unitType?.includes(item?.unitType),
          expectValue: true,
        });
      }

      if (this.turnoversFilters.workflowPhaseType?.length) {
        compareParams.push({
          currentValue: this.turnoversFilters.workflowPhaseType.includes(item?.workflowPhaseType),
          expectValue: true,
        });
      }

      if (this.turnoversFilters?.finish) {
        compareParams.push({
          currentValue: item.turnoverData?.newFinish || item.turnoverData?.finish,
          expectValue: this.turnoversFilters?.finish,
        });
      }

      if (this.turnoversFilters?.moveOutType) {
        compareParams.push({
          currentValue: item.turnoverData?.unitStatus,
          expectValue: this.turnoversFilters?.moveOutType,
        });
      }

      if (this.turnoversFilters?.properties) {
        compareParams.push({
          currentValue: item.propertyId,
          expectValue: this.turnoversFilters?.properties,
        });
      }

      if (isBoolean(isEviction)) {
        compareParams.push({
          currentValue: item.turnoverData?.hasEvictLabel,
          expectValue: isEviction,
        });
      }

      if (this.turnoversFilters?.label?.length) {
        compareParams.push({
          compare: () => {
            return this.turnoversFilters.label
              .map(label => {
                switch (label) {
                  case LabelEnumType.Available: {
                    return item.turnoverData.hasAvailableOnLabel && !item.turnoverData.hasMoveInLabel;
                  }
                  case LabelEnumType.MoveIn: {
                    return item.turnoverData.hasMoveInLabel;
                  }
                  case LabelEnumType.MoveOut: {
                    return item.turnoverData.hasMoveOutLabel;
                  }
                  case LabelEnumType.Occupied: {
                    return item.turnoverData.hasOccupiedLabel;
                  }
                  case LabelEnumType.Eviction: {
                    return item.turnoverData.hasEvictLabel;
                  }
                }
              })
              .some(i => i);
          },
        });
      }

      if (paramsCompare(compareParams)) {
        section.push({
          item,
          visible: notHidden,
        });
      } else {
        section.push({
          item,
          visible: false,
        });
      }
    }
  }

  private sortSections() {
    this.evictionSection = this.moveOutSort(this.evictionSection);
    this.noticeSection = this.moveOutSort(this.noticeSection);
    this.moveOutSection = this.moveOutSort(this.moveOutSection);
    this.makeReadySection = this.defaultSort(this.makeReadySection);
    this.renoSection = this.defaultSort(this.renoSection);
    this.punchSection = this.defaultSort(this.punchSection);
    this.approvalSection = this.defaultSort(this.approvalSection);
    this.leaseUpSection = this.defaultSort(this.leaseUpSection);
    this.moveInSection = this.moveInSort(this.moveInSection);
  }

  private defaultSort(list: RestTurnoverTaskModelExt[]): RestTurnoverTaskModelExt[] {
    return list.sort((item1, item2) => {
      return (
        this.compareUnitHold(item1.item, item2.item) ||
        this.compareUnitDown(item1.item, item2.item) ||
        this.compareOccupied(item1.item, item2.item) ||
        this.comparePriority(item1.item, item2.item) ||
        this.compareElapsedDays(item1.item, item2.item)
      );
    });
  }

  private moveOutSort(list: RestTurnoverTaskModelExt[]): RestTurnoverTaskModelExt[] {
    return list.sort((item1, item2) => {
      return (
        this.compareEvict(item1.item, item2.item) ||
        this.compareUnitHold(item1.item, item2.item) ||
        this.compareUnitDown(item1.item, item2.item) ||
        this.compareElapsedDays(item1.item, item2.item) ||
        this.comparePriority(item1.item, item2.item) ||
        this.compareOccupied(item1.item, item2.item) ||
        this.compareDate(item1?.item?.turnoverData?.dateMoveOut, item2?.item?.turnoverData?.dateMoveOut)
      );
    });
  }

  private moveInSort(list: RestTurnoverTaskModelExt[]): RestTurnoverTaskModelExt[] {
    return list.sort((item1, item2) => {
      return (
        this.compareUnitHold(item1.item, item2.item) ||
        this.compareUnitDown(item1.item, item2.item) ||
        this.compareOccupied(item1.item, item2.item) ||
        this.compareDate(item1.item.turnoverData?.dateMoveIn, item2.item.turnoverData?.dateMoveIn) ||
        this.compareElapsedDays(item1.item, item2.item)
      );
    });
  }

  private compareEvict(item1: RestTurnoverTaskModel, item2: RestTurnoverTaskModel): number {
    const isEvict1 = item1.turnoverData?.hasEvictLabel ? 1 : 0;
    const isEvict2 = item2.turnoverData?.hasEvictLabel ? 1 : 0;
    return isEvict1 - isEvict2;
  }

  private compareUnitDown(item1: RestTurnoverTaskModel, item2: RestTurnoverTaskModel): number {
    const isUnitDown1 = item1?.turnoverData?.isUnitDown ? 1 : 0;
    const isUnitDown2 = item2?.turnoverData?.isUnitDown ? 1 : 0;
    return isUnitDown1 - isUnitDown2;
  }

  private compareUnitHold(item1: RestTurnoverTaskModel, item2: RestTurnoverTaskModel): number {
    const isUnitOnHold1 = item1?.turnoverData?.isUnitOnHold ? 1 : 0;
    const isUnitOnHold2 = item2?.turnoverData?.isUnitOnHold ? 1 : 0;
    return isUnitOnHold1 - isUnitOnHold2;
  }

  private comparePriority(item1: RestTurnoverTaskModel, item2: RestTurnoverTaskModel): number {
    const priority2 =
      getCurrentWorkflowStep(item2.turnoverData.activeWorkflowStepType, item2.turnoverData.workflow)?.priority ||
      PriorityType.None;

    const priority1 =
      getCurrentWorkflowStep(item1.turnoverData.activeWorkflowStepType, item1.turnoverData.workflow)?.priority ||
      PriorityType.None;

    return priority2 - priority1;
  }

  private compareElapsedDays(item1: RestTurnoverTaskModel, item2: RestTurnoverTaskModel): number {
    const time2 = getTimeInStep(item2.turnoverData.activeWorkflowStepType, item2.turnoverData?.timerInstance);
    const time1 = getTimeInStep(item1.turnoverData.activeWorkflowStepType, item1.turnoverData?.timerInstance);
    return time2 - time1;
  }

  private compareDate(item1: Date | string, item2: Date | string): number {
    const date1 = new Date(item1).getTime() || 0;
    const date2 = new Date(item2).getTime() || 0;
    return date1 - date2;
  }

  private compareOccupied(item1: RestTurnoverTaskModel, item2: RestTurnoverTaskModel): number {
    return item1.turnoverData.occupiedCountdown - item2.turnoverData.occupiedCountdown;
  }

  openTurnover(item: RestTurnoverTaskModel) {
    const params: Params = { [RouteData.KANBAN_SECTION]: item?.propertyId || null };
    const currentParams = this.activatedRoute.snapshot.queryParams;
    this.router
      .navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { ...currentParams, ...params },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
      .then(() => {
        const queryParams = this.activatedRoute.snapshot.queryParams;
        this.router
          .navigate([AppRoutes.TURNOVERS, item?.turnoverData?.id], {
            queryParams,
            queryParamsHandling: 'merge',
          })
          .then();
      });
  }

  private scrollIntoThisSection() {
    const section = document.querySelector(`#${this.sectionId} #section-title`) as HTMLElement;
    ScrollIntoHTMLElement(section, { block: 'nearest', behavior: 'smooth' });
  }

  private checkSectionToExpand(sectionToExpand: number) {
    if (sectionToExpand === this.kanbanListItem.propertyId) {
      this.store.dispatch(toggleSingleSection({ propertyId: this.kanbanListItem.propertyId, active: true }));
      this.scrollIntoThisSection();
    }
  }

  previewInspectedItem(inspectedItem: TaskInspectionItem) {
    this.previewContentCss = null;
    this.stepsBeforeTask = null;
    this.previewCss = '';
    this.cdr.detectChanges();

    const containerRect = this.sectionContainer.nativeElement.getBoundingClientRect();
    if (inspectedItem) {
      const tileRect = inspectedItem?.tileRect;

      this.previewContentCss = inspectedItem
        ? {
            top: `${tileRect.top - containerRect.top}px`,
            height: `${tileRect.height}px`,
            width: `${tileRect.x - containerRect.x - 24}px`,
          }
        : null;

      this.stepsBeforeTask = this.extendMakeReadyColumn
        ? this.getStepBeforeForMakeReadyExt(inspectedItem.task, inspectedItem.skipMakeReadyColumns)
        : this.getStepBeforeTask(inspectedItem.task);
    }

    this.cdr.detectChanges();
  }

  animatePreview() {
    setTimeout(() => {
      this.previewCss = 'animate';
      this.cdr.detectChanges();
    }, 200);
  }

  toggleAllSections() {
    this.isAllExpanded = !this.isAllExpanded;
    this.store.dispatch(toggleAllSections({ active: this.isAllExpanded }));
  }

  private getStepBefore(task: RestTurnoverTaskModel): RestWorkflowInstanceModel[] {
    return task.turnoverData.workflow
      .filter(item => item.workflowStepEnumType < task.turnoverData.activeWorkflowStepType)
      .filter(
        item =>
          item.workflowStepEnumType !== WorkflowStepEnumType.Marketing &&
          item.workflowStepEnumType !== WorkflowStepEnumType.Bid
      )
      .filter(
        item =>
          !this.hiddenNotReadyColumns.has(item.workflowStepEnumType) &&
          !this.hiddenReadyColumns.has(item.workflowStepEnumType)
      )
      .sort(
        (a: RestWorkflowInstanceModel, b: RestWorkflowInstanceModel) => a.workflowStepEnumType - b.workflowStepEnumType
      );
  }

  private getStepBeforeTask(task: RestTurnoverTaskModel): StepsBeforeTask {
    const stepsBefore = {
      unitName: task.unitName,
      unitRent: task.turnoverData?.newRent || task.turnoverData?.marketRent,
      stepBefore: this.getStepBefore(task),
      timerInstance: task.turnoverData?.timerInstance,
      dateMoveOut: task.turnoverData.dateMoveOut,
    };
    const hiddenEvictionStep = { ...first(stepsBefore.stepBefore) };
    hiddenEvictionStep.fakeHiddenStep = true;
    stepsBefore.stepBefore.unshift(hiddenEvictionStep);
    return stepsBefore;
  }

  private getStepBeforeForMakeReadyExt(task: RestTurnoverTaskModel, skipColumns = 0): StepsBeforeTask {
    const stepsBeforeTask = this.getStepBeforeTask(task);

    const isMakeReadyTask = task.turnoverData?.activeWorkflowStepType === WorkflowStepEnumType.MakeReady;
    if (isMakeReadyTask && skipColumns === 0) {
      return stepsBeforeTask;
    }
    if (isMakeReadyTask && skipColumns > 0) {
      const hiddenStep = { ...last(stepsBeforeTask.stepBefore) };
      hiddenStep.fakeHiddenStep = true;

      for (let i = 0; i < skipColumns; i++) {
        stepsBeforeTask.stepBefore.push({ ...hiddenStep });
      }
      return stepsBeforeTask;
    }

    const makeReadyIdx = stepsBeforeTask.stepBefore.findIndex(
      item => item.workflowStepEnumType === WorkflowStepEnumType.MakeReady
    );
    if (makeReadyIdx === -1) return stepsBeforeTask;

    const hiddenStep = { ...stepsBeforeTask.stepBefore[makeReadyIdx] };
    hiddenStep.fakeHiddenStep = true;
    stepsBeforeTask.stepBefore.splice(makeReadyIdx, 0, hiddenStep, hiddenStep);
    return stepsBeforeTask;
  }

  trackByFn(index: number, item: RestTurnoverTaskModelExt) {
    return item.item.id;
  }
}
